import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Clock, Users, LogOut, Plus, Edit2, Trash2, X, Check,
  Upload, AlertCircle, CheckCircle, Moon, KeyRound, Eye, EyeOff, Globe
} from 'lucide-react';
import {
  getMosques, createMosque, updateMosque, deleteMosque,
  getPrayerTimesByMosque, createPrayerTime, updatePrayerTime,
  getJummahTimes, createJummahTime, updateJummahTime, deleteJummahTime,
  getGlobalTimes, saveGlobalTime, deleteGlobalTime,
} from '../../api';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

// ─── Toast ──────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.9 }}
      className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border ${
        type === 'success'
          ? 'bg-emerald-900/80 border-emerald-500/40 text-emerald-300'
          : 'bg-red-900/80 border-red-500/40 text-red-300'
      }`}
    >
      {type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

// ─── Modal ──────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        className="glass-card-light rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-white/15 shadow-2xl"
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </motion.div>
    </motion.div>
  );
}

// ─── Input ──────────────────────────────────────────────────────────────
function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-emerald-400 mb-1.5">{label}</label>
      {props.as === 'textarea' ? (
        <textarea className="input-dark w-full rounded-xl px-4 py-2.5 resize-none" rows={3} {...props} as={undefined} />
      ) : (
        <input className="input-dark w-full rounded-xl px-4 py-2.5" {...props} />
      )}
    </div>
  );
}

// ─── Confirm Delete ─────────────────────────────────────────────────────
function ConfirmDelete({ name, onConfirm, onCancel }) {
  return (
    <Modal title="Confirm Delete" onClose={onCancel}>
      <p className="text-gray-300 mb-6">
        Are you sure you want to delete <span className="text-white font-semibold">"{name}"</span>? This action cannot be undone.
      </p>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-xl glass-card text-gray-300 hover:text-white transition-colors text-sm">
          Cancel
        </button>
        <button onClick={onConfirm} className="px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors flex items-center gap-2">
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      </div>
    </Modal>
  );
}

// ─── MOSQUES TAB ────────────────────────────────────────────────────────
function MosquesTab({ showToast }) {
  const [mosques, setMosques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMosque, setEditingMosque] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '', email: '', about: '' });
  const [imageFile, setImageFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const imageRef = useRef();
  const pdfRef = useRef();

  const load = () => {
    setLoading(true);
    getMosques().then((r) => setMosques(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingMosque(null);
    setForm({ name: '', address: '', phone: '', email: '', about: '' });
    setImageFile(null);
    setPdfFile(null);
    setModalOpen(true);
  };

  const openEdit = (mosque) => {
    setEditingMosque(mosque);
    setForm({ name: mosque.name, address: mosque.address, phone: mosque.phone || '', email: mosque.email || '', about: mosque.about || '' });
    setImageFile(null);
    setPdfFile(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.address) {
      showToast('Name and address are required.', 'error');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (imageFile) fd.append('timetableImage', imageFile);
      if (pdfFile) fd.append('timetablePdf', pdfFile);

      if (editingMosque) {
        await updateMosque(editingMosque._id, fd);
        showToast('Mosque updated successfully!', 'success');
      } else {
        await createMosque(fd);
        showToast('Mosque added successfully!', 'success');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save mosque.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMosque(deleteTarget._id);
      showToast('Mosque deleted.', 'success');
      setDeleteTarget(null);
      load();
    } catch (err) {
      showToast('Failed to delete mosque.', 'error');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Mosques</h2>
        <button onClick={openAdd} className="btn-primary px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Mosque
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-xl shimmer" />)}
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-dark">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Timetable</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mosques.map((m) => (
                  <tr key={m._id}>
                    <td className="font-semibold text-white">{m.name}</td>
                    <td className="text-gray-400 text-xs">{m.address}</td>
                    <td className="text-gray-400 text-xs">{m.phone || '—'}</td>
                    <td>
                      <div className="flex gap-2">
                        {m.timetableImageUrl && <span className="badge-emerald">Image</span>}
                        {m.timetablePdfUrl && <span className="badge-gold">PDF</span>}
                        {!m.timetableImageUrl && !m.timetablePdfUrl && <span className="text-gray-600 text-xs">None</span>}
                      </div>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(m)} className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteTarget(m)} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {modalOpen && (
          <Modal title={editingMosque ? 'Edit Mosque' : 'Add Mosque'} onClose={() => setModalOpen(false)}>
            <div className="space-y-4">
              <Field label="Mosque Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Oldham Central Mosque" />
              <Field label="Address *" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="e.g. 22 Roundthorn Road, OL1 4AA" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0161 000 0000" />
                <Field label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="info@mosque.co.uk" />
              </div>
              <Field label="About" as="textarea" value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} placeholder="Brief description of the mosque..." />

              {/* File Uploads */}
              <div>
                <label className="block text-sm font-medium text-emerald-400 mb-1.5">Timetable Image</label>
                <div
                  onClick={() => imageRef.current.click()}
                  className="border-2 border-dashed border-white/15 rounded-xl p-4 text-center cursor-pointer hover:border-emerald-500/40 transition-colors"
                >
                  <Upload className="w-6 h-6 text-gray-500 mx-auto mb-1" />
                  <p className="text-gray-400 text-sm">
                    {imageFile ? imageFile.name : 'Click to upload image (JPG, PNG)'}
                  </p>
                  <input ref={imageRef} type="file" accept="image/*" className="hidden" onChange={(e) => setImageFile(e.target.files[0])} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-emerald-400 mb-1.5">Timetable PDF</label>
                <div
                  onClick={() => pdfRef.current.click()}
                  className="border-2 border-dashed border-white/15 rounded-xl p-4 text-center cursor-pointer hover:border-gold-500/40 transition-colors"
                >
                  <Upload className="w-6 h-6 text-gray-500 mx-auto mb-1" />
                  <p className="text-gray-400 text-sm">
                    {pdfFile ? pdfFile.name : 'Click to upload PDF'}
                  </p>
                  <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => setPdfFile(e.target.files[0])} />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-xl glass-card text-gray-300 hover:text-white transition-colors text-sm">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="btn-primary px-5 py-2 rounded-xl text-sm font-medium flex items-center gap-2 disabled:opacity-60">
                  {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
                  {editingMosque ? 'Update' : 'Add'} Mosque
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Delete Confirm */}
      <AnimatePresence>
        {deleteTarget && (
          <ConfirmDelete name={deleteTarget.name} onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── PRAYER TIMES TAB ───────────────────────────────────────────────────
function PrayerTimesTab({ showToast }) {
  const [mosques, setMosques] = useState([]);
  const [selectedMosque, setSelectedMosque] = useState('');
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [existingRecord, setExistingRecord] = useState(null);
  const [form, setForm] = useState({ fajr: '', sunrise: '', dhuhr: '', asr: '', maghrib: '', isha: '' });
  const [saving, setSaving] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(false);

  useEffect(() => {
    getMosques().then((r) => {
      setMosques(r.data);
      if (r.data.length > 0) setSelectedMosque(r.data[0]._id);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedMosque || !selectedDate) return;
    setLoadingRecord(true);
    // Fetch prayer times for this mosque and check if date exists
    getPrayerTimesByMosque(selectedMosque).then((r) => {
      const record = r.data.find((t) => t.date === selectedDate);
      if (record) {
        setExistingRecord(record);
        setForm({
          fajr: record.fajr || '',
          sunrise: record.sunrise || '',
          dhuhr: record.dhuhr || '',
          asr: record.asr || '',
          maghrib: record.maghrib || '',
          isha: record.isha || '',
        });
      } else {
        setExistingRecord(null);
        setForm({ fajr: '', sunrise: '', dhuhr: '', asr: '', maghrib: '', isha: '' });
      }
    }).catch(console.error).finally(() => setLoadingRecord(false));
  }, [selectedMosque, selectedDate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { mosqueId: selectedMosque, date: selectedDate, ...form };
      if (existingRecord) {
        await updatePrayerTime(existingRecord._id, data);
        showToast('Prayer times updated!', 'success');
      } else {
        await createPrayerTime(data);
        showToast('Prayer times saved!', 'success');
      }
    } catch (err) {
      showToast('Failed to save prayer times.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const prayers = [
    { key: 'fajr', label: 'Fajr', icon: '🌙' },
    { key: 'sunrise', label: 'Sunrise', icon: '🌅' },
    { key: 'dhuhr', label: 'Dhuhr', icon: '☀️' },
    { key: 'asr', label: 'Asr', icon: '🌤' },
    { key: 'maghrib', label: 'Maghrib', icon: '🌇' },
    { key: 'isha', label: 'Isha', icon: '🌃' },
  ];

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-6">Prayer Times</h2>

      <div className="glass-card-light rounded-2xl p-6">
        {/* Selectors */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-emerald-400 mb-1.5">Mosque</label>
            <select
              value={selectedMosque}
              onChange={(e) => setSelectedMosque(e.target.value)}
              className="input-dark w-full rounded-xl px-4 py-2.5 cursor-pointer"
            >
              {mosques.map((m) => (
                <option key={m._id} value={m._id} style={{ background: '#0d1f3c' }}>{m.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-emerald-400 mb-1.5">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="input-dark w-full rounded-xl px-4 py-2.5 cursor-pointer"
            />
          </div>
        </div>

        {existingRecord && (
          <div className="mb-4 flex items-center gap-2 text-emerald-400 text-sm">
            <CheckCircle className="w-4 h-4" />
            Existing record found — editing
          </div>
        )}

        {loadingRecord ? (
          <div className="flex items-center gap-2 text-gray-400 py-4">
            <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            Loading...
          </div>
        ) : (
          <>
            {/* Prayer time inputs */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
              {prayers.map(({ key, label, icon }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">
                    <span className="mr-1">{icon}</span>{label}
                  </label>
                  <input
                    type="time"
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="input-dark w-full rounded-xl px-4 py-2.5"
                  />
                </div>
              ))}
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary px-6 py-2.5 rounded-xl font-medium text-sm flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
              {existingRecord ? 'Update' : 'Save'} Prayer Times
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── JUMMAH TAB ─────────────────────────────────────────────────────────
function JummahTab({ showToast }) {
  const [jummahTimes, setJummahTimes] = useState([]);
  const [mosques, setMosques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ mosqueId: '', khutbahTime: '', jummahTime: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([getJummahTimes(), getMosques()])
      .then(([jRes, mRes]) => { setJummahTimes(jRes.data); setMosques(mRes.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingEntry(null);
    setForm({ mosqueId: mosques[0]?._id || '', khutbahTime: '', jummahTime: '', notes: '' });
    setModalOpen(true);
  };

  const openEdit = (entry) => {
    setEditingEntry(entry);
    setForm({
      mosqueId: entry.mosqueId?._id || entry.mosqueId,
      khutbahTime: entry.khutbahTime || '',
      jummahTime: entry.jummahTime || '',
      notes: entry.notes || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.mosqueId) { showToast('Please select a mosque.', 'error'); return; }
    setSaving(true);
    try {
      if (editingEntry) {
        await updateJummahTime(editingEntry._id, form);
        showToast('Jummah time updated!', 'success');
      } else {
        await createJummahTime(form);
        showToast('Jummah time added!', 'success');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      showToast('Failed to save.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteJummahTime(deleteTarget._id);
      showToast('Jummah time deleted.', 'success');
      setDeleteTarget(null);
      load();
    } catch (err) {
      showToast('Failed to delete.', 'error');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Jumu'ah Times</h2>
        <button onClick={openAdd} className="btn-primary px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Jumu'ah Time
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-xl shimmer" />)}</div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-dark">
              <thead>
                <tr>
                  <th>Mosque</th>
                  <th>Khutbah</th>
                  <th>Jumu'ah</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jummahTimes.map((entry) => (
                  <tr key={entry._id}>
                    <td className="font-semibold text-white">{entry.mosqueId?.name}</td>
                    <td><span className="text-gold-400 font-semibold">{entry.khutbahTime || '—'}</span></td>
                    <td><span className="text-emerald-400 font-semibold">{entry.jummahTime || '—'}</span></td>
                    <td className="text-gray-400 text-xs max-w-xs truncate">{entry.notes || '—'}</td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(entry)} className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteTarget(entry)} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AnimatePresence>
        {modalOpen && (
          <Modal title={editingEntry ? "Edit Jumu'ah Time" : "Add Jumu'ah Time"} onClose={() => setModalOpen(false)}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-emerald-400 mb-1.5">Mosque</label>
                <select
                  value={form.mosqueId}
                  onChange={(e) => setForm({ ...form, mosqueId: e.target.value })}
                  className="input-dark w-full rounded-xl px-4 py-2.5"
                >
                  <option value="">Select mosque...</option>
                  {mosques.map((m) => (
                    <option key={m._id} value={m._id} style={{ background: '#0d1f3c' }}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-emerald-400 mb-1.5">Khutbah Time</label>
                  <input type="time" value={form.khutbahTime} onChange={(e) => setForm({ ...form, khutbahTime: e.target.value })} className="input-dark w-full rounded-xl px-4 py-2.5" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-emerald-400 mb-1.5">Jumu'ah Time</label>
                  <input type="time" value={form.jummahTime} onChange={(e) => setForm({ ...form, jummahTime: e.target.value })} className="input-dark w-full rounded-xl px-4 py-2.5" />
                </div>
              </div>
              <Field label="Notes" as="textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any special notes..." />
              <div className="flex gap-3 justify-end pt-2">
                <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-xl glass-card text-gray-300 hover:text-white transition-colors text-sm">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="btn-primary px-5 py-2 rounded-xl text-sm font-medium flex items-center gap-2 disabled:opacity-60">
                  {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
                  {editingEntry ? 'Update' : 'Add'} Jumu'ah
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteTarget && (
          <ConfirmDelete
            name={deleteTarget.mosqueId?.name}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── MAIN DASHBOARD ─────────────────────────────────────────────────────
// ─── Global Times Tab ────────────────────────────────────────────────────
const PRAYER_FIELDS = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];

function GlobalTimesTab({ showToast }) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [date, setDate] = useState(today);
  const [form, setForm] = useState({ fajr: '', sunrise: '', dhuhr: '', asr: '', maghrib: '', isha: '' });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchEntries = () => {
    getGlobalTimes().then((res) => setEntries(res.data)).catch(() => {});
  };

  useEffect(() => { fetchEntries(); }, []);

  // When date changes, prefill form if entry exists
  useEffect(() => {
    const existing = entries.find((e) => e.date === date);
    if (existing) {
      setForm({ fajr: existing.fajr || '', sunrise: existing.sunrise || '', dhuhr: existing.dhuhr || '', asr: existing.asr || '', maghrib: existing.maghrib || '', isha: existing.isha || '' });
    } else {
      setForm({ fajr: '', sunrise: '', dhuhr: '', asr: '', maghrib: '', isha: '' });
    }
  }, [date, entries]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveGlobalTime({ date, ...form });
      showToast('Global times saved successfully.');
      fetchEntries();
    } catch {
      showToast('Failed to save times.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteGlobalTime(id);
      showToast('Entry deleted.');
      fetchEntries();
    } catch {
      showToast('Failed to delete.', 'error');
    }
  };

  return (
    <div className="space-y-8">
      {/* Info banner */}
      <div className="glass-card rounded-xl p-4 border border-emerald-500/20 flex items-start gap-3">
        <Globe className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
        <p className="text-gray-300 text-sm">
          These times appear in the <span className="text-emerald-400 font-medium">hero countdown</span> on the home page.
          They are independent of individual mosque times. If no global time is set for today, the homepage falls back to the earliest mosque time.
        </p>
      </div>

      {/* Form */}
      <div className="glass-card rounded-2xl p-6">
        <h2 className="text-white font-semibold text-lg mb-5">Set Times for a Date</h2>
        <form onSubmit={handleSave} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-emerald-400 mb-2">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input-dark rounded-xl px-4 py-3 w-full sm:w-auto"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {PRAYER_FIELDS.map((p) => (
              <div key={p}>
                <label className="block text-sm font-medium text-gray-300 mb-2 capitalize">{p}</label>
                <input
                  type="time"
                  value={form[p]}
                  onChange={(e) => setForm({ ...form, [p]: e.target.value })}
                  className="input-dark rounded-xl px-4 py-3 w-full"
                />
              </div>
            ))}
          </div>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary px-6 py-3 rounded-xl font-semibold flex items-center gap-2"
          >
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
            Save Times
          </button>
        </form>
      </div>

      {/* Existing entries */}
      {entries.length > 0 && (
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5">
            <h2 className="text-white font-semibold">Saved Global Times</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-dark">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Fajr</th>
                  <th>Sunrise</th>
                  <th>Dhuhr</th>
                  <th>Asr</th>
                  <th>Maghrib</th>
                  <th>Isha</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry._id} className={entry.date === today ? 'bg-emerald-500/5' : ''}>
                    <td>
                      <span className="font-medium text-white">{entry.date}</span>
                      {entry.date === today && <span className="ml-2 text-xs text-emerald-400">(today)</span>}
                    </td>
                    {PRAYER_FIELDS.map((p) => (
                      <td key={p} className="text-gray-300">{entry[p] || '—'}</td>
                    ))}
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => setDate(entry.date)} className="text-emerald-400 hover:text-emerald-300 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(entry._id)} className="text-red-400 hover:text-red-300 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Change Password Tab ─────────────────────────────────────────────────
function ChangePasswordTab({ showToast }) {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [show, setShow] = useState({ current: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      showToast('New passwords do not match.', 'error');
      return;
    }
    if (form.newPassword.length < 6) {
      showToast('New password must be at least 6 characters.', 'error');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      showToast('Password updated successfully.');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update password.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const PasswordField = ({ label, field, showKey }) => (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
      <div className="relative">
        <input
          type={show[showKey] ? 'text' : 'password'}
          value={form[field]}
          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
          className="input-dark w-full rounded-xl px-4 py-3 pr-12"
          placeholder="••••••••"
          required
        />
        <button
          type="button"
          onClick={() => setShow({ ...show, [showKey]: !show[showKey] })}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
        >
          {show[showKey] ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-md">
      <div className="glass-card rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-white font-semibold">Change Password</h2>
            <p className="text-gray-400 text-sm">Update your admin password</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordField label="Current Password" field="currentPassword" showKey="current" />
          <PasswordField label="New Password" field="newPassword" showKey="new" />
          <PasswordField label="Confirm New Password" field="confirmPassword" showKey="confirm" />
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 mt-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <><Check className="w-5 h-5" /> Update Password</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

const TABS = [
  { key: 'mosques', label: 'Mosques', icon: Building2 },
  { key: 'prayer-times', label: 'Prayer Times', icon: Clock },
  { key: 'jummah', label: "Jumu'ah", icon: Users },
  { key: 'global-times', label: 'Global Times', icon: Globe },
  { key: 'password', label: 'Change Password', icon: KeyRound },
];

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('mosques');
  const [toasts, setToasts] = useState([]);
  const { admin, logoutAdmin } = useAuth();
  const navigate = useNavigate();

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Toasts */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
        <AnimatePresence>
          {toasts.map((t) => (
            <Toast key={t.id} message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
          ))}
        </AnimatePresence>
      </div>

      <div className="flex h-[calc(100vh-64px)]">
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0 hidden md:flex flex-col navbar-blur border-r border-emerald-500/10">
          {/* Brand */}
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
                <Moon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-white font-bold text-sm">Admin Panel</div>
                <div className="text-emerald-400 text-xs">@{admin?.username}</div>
              </div>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-4 space-y-1">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`sidebar-link w-full ${activeTab === key ? 'active' : ''}`}
              >
                <Icon className="w-5 h-5" />
                {label}
              </button>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-white/5">
            <button
              onClick={handleLogout}
              className="sidebar-link w-full text-red-400 hover:bg-red-500/10 hover:text-red-300"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Mobile tabs */}
          <div className="md:hidden flex gap-2 p-4 border-b border-white/5 overflow-x-auto navbar-blur">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === key
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap text-red-400 hover:bg-red-500/10 transition-all ml-auto"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6 max-w-6xl mx-auto">
            {/* Page header */}
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                {TABS.find((t) => t.key === activeTab)?.label}
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                Manage {TABS.find((t) => t.key === activeTab)?.label.toLowerCase()} for Oldham Prayer Times
              </p>
            </div>

            {/* Tab content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {activeTab === 'mosques' && <MosquesTab showToast={showToast} />}
                {activeTab === 'prayer-times' && <PrayerTimesTab showToast={showToast} />}
                {activeTab === 'jummah' && <JummahTab showToast={showToast} />}
                {activeTab === 'global-times' && <GlobalTimesTab showToast={showToast} />}
                {activeTab === 'password' && <ChangePasswordTab showToast={showToast} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
